import { useState } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DetailList } from '@/components/ui/DetailList';
import { Icon } from '@/components/ui/Icon';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateMessage';
import { useToast } from '@/components/ui/Toast';
import { formatDate, formatTime, timestampToDateOnly } from '@/utils/date';
import { getErrorMessage } from '@/utils/errors';
import { pluralize } from '@/utils/text';

import type { EnrolSlot } from '../../api/attendance.api';
import {
  useAttendanceDevices,
  useCommandProgress,
  useEnrolMember,
  useLinkMachineUser,
  useMemberAttendance,
} from '../../hooks/useAttendance';
import type { CommandProgress, MachineUser } from '../../schemas/attendance.schema';
import { MachineUsersDialog } from '../MachineUsersDialog';
import styles from './GymAccessCard.module.css';

export interface GymAccessCardProps {
  member: { id: number; name: string };
  /** `attendance:manage`: link, unlink and enrol. */
  canManage: boolean;
}

/** Whether the door lets them in, as last confirmed by the machine. */
function AccessBadge({ user }: { user: MachineUser }) {
  if (user.accessEnabled === true) {
    return (
      <Badge tone="success" dot>
        Allowed
      </Badge>
    );
  }
  if (user.accessEnabled === false) {
    return (
      <Badge tone="danger" dot>
        Blocked at the door
      </Badge>
    );
  }
  return (
    <Badge tone="neutral" dot>
      Updating…
    </Badge>
  );
}

/** Where a remote enrolment has got to, in words for the front desk. */
function EnrolStatus({ progress }: { progress: CommandProgress | undefined }) {
  if (!progress || progress.status === 'pending') {
    return <Alert tone="info">Waiting for the machine to pick this up (up to 20 seconds)…</Alert>;
  }
  if (progress.status === 'sent') {
    return <Alert tone="info">The machine has it. Its enrol screen is opening…</Alert>;
  }
  if (progress.status === 'done') {
    return (
      <Alert tone="success" title="Go to the machine">
        It is showing the enrol screen. Ask the member to look at it, or place the finger.
      </Alert>
    );
  }
  return (
    <Alert tone="danger" title="The machine didn't take it">
      {progress.resultNote ?? 'Try again, or enrol on the machine itself.'}
    </Alert>
  );
}

/**
 * The member on the punch machine: are they let in, when did they last come, and how often.
 * Staff link the member to their machine user here, or start an enrolment from the desk.
 */
export function GymAccessCard({ member, canManage }: GymAccessCardProps) {
  const toast = useToast();
  const attendance = useMemberAttendance(member.id);
  const device = useAttendanceDevices().data?.[0];
  const controls = device?.controls ?? [];
  const link = useLinkMachineUser();
  const enrol = useEnrolMember(member.id);
  const [enrolId, setEnrolId] = useState<number | null>(null);
  const progress = useCommandProgress(enrolId);
  const [finding, setFinding] = useState(false);

  const canEnrol = canManage && controls.includes('enrol');
  const startEnrol = (slot: EnrolSlot) => {
    enrol.mutate(slot, {
      onSuccess: (command) => {
        setEnrolId(command.id);
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      },
    });
  };

  const data = attendance.data;
  const user = data?.machineUser ?? null;

  const menu: MenuItem[] = [];
  if (canEnrol) {
    menu.push(
      {
        label: 'Register face',
        icon: 'face',
        onSelect: () => {
          startEnrol({ method: 'face' });
        },
      },
      {
        label: 'Register a finger',
        icon: 'fingerprint',
        onSelect: () => {
          startEnrol({ method: 'finger', finger: 1 });
        },
      },
    );
  }
  if (canManage && user) {
    menu.push({
      label: 'Unlink from the machine',
      icon: 'close',
      tone: 'danger',
      onSelect: () => {
        link.mutate(
          { userId: user.id, memberId: null },
          {
            onSuccess: () => {
              toast.success('Unlinked from the machine');
            },
            onError: (error) => {
              toast.error(getErrorMessage(error));
            },
          },
        );
      },
    });
  }

  return (
    <Card
      title="Gym access"
      description={
        user
          ? `On the punch machine as ${user.name ?? user.deviceUserId}.`
          : 'The face & finger door.'
      }
      actions={
        menu.length > 0 && user ? (
          <Menu
            label="Gym access actions"
            trigger={<Icon name="more" size={20} />}
            triggerClassName={styles.menuTrigger}
            items={menu}
          />
        ) : undefined
      }
    >
      {attendance.isError ? (
        <ErrorState
          compact
          error={attendance.error}
          onRetry={() => {
            void attendance.refetch();
          }}
        />
      ) : data === undefined ? (
        <LoadingState compact />
      ) : !user ? (
        <EmptyState
          compact
          icon="fingerprint"
          title="Not on the machine yet"
          description="Link them to their person on the punch machine to see visits and control the door."
          action={
            canManage ? (
              <div className={styles.actions}>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="link"
                  onClick={() => {
                    setFinding(true);
                  }}
                >
                  Find on machine
                </Button>
                {canEnrol && (
                  <Button
                    size="sm"
                    icon="face"
                    isLoading={enrol.isPending}
                    onClick={() => {
                      startEnrol({ method: 'face' });
                    }}
                  >
                    Register face
                  </Button>
                )}
              </div>
            ) : undefined
          }
        />
      ) : (
        <div className={styles.body}>
          <DetailList
            items={[
              {
                label: 'Door',
                value: controls.includes('access') ? (
                  <AccessBadge user={user} />
                ) : (
                  'Not managed from here'
                ),
              },
              { label: 'Machine ID', value: <span className="tabular">{user.deviceUserId}</span> },
              {
                label: 'Last visit',
                value: data.lastVisitAt
                  ? `${formatDate(timestampToDateOnly(data.lastVisitAt))}, ${formatTime(data.lastVisitAt)}`
                  : 'Never came in',
              },
              { label: 'This month', value: pluralize(data.visitsThisMonth, 'visit') },
            ]}
          />
          {data.recentVisits.length > 0 && (
            <div className={styles.visits}>
              <span className={styles.visitsLabel}>Recent visits</span>
              <ul className={styles.chips}>
                {data.recentVisits.map((visit) => (
                  <li key={visit} className={styles.chip}>
                    {formatDate(timestampToDateOnly(visit))}
                    <span className={styles.chipTime}>{formatTime(visit)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {enrolId !== null && <EnrolStatus progress={progress.data} />}

      <MachineUsersDialog
        open={finding}
        forMember={member}
        onClose={() => {
          setFinding(false);
        }}
      />
    </Card>
  );
}
